import React from 'react';
import { Link } from 'react-router-dom';
import './action.scss';

type ActionVariant = 'solid' | 'outline' | 'ghost';
type ActionTone = 'light' | 'dark';
type ActionSize = 'sm' | 'md' | 'lg';
type IconPosition = 'left' | 'right';

type CommonProps = {
    children?: React.ReactNode;
    extraClass?: string;
    variant?: ActionVariant;
    tone?: ActionTone; // default: light
    size?: ActionSize; // default: md
    fullWidth?: boolean; // default: false
    align?: 'left' | 'center' | 'right'; // default: center
    iconSrc?: string;
    iconAlt?: string;
    iconPosition?: IconPosition; // default: left
    iconSize?: number; // px (optional)
    disabled?: boolean; // works for button, and emulated for links
    loading?: boolean; // disables interaction and shows spinner
    title?: string;
    /** IMPORTANT: for icon-only обязательно задавай ariaLabel */
    ariaLabel?: string;
};

type ButtonProps = CommonProps & {
    as: 'button';
    type?: 'button' | 'submit' | 'reset';
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
};
type InternalLinkProps = CommonProps & {
    as: 'link';
    to: string;
    replace?: boolean;
    state?: unknown;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};
type ExternalLinkProps = CommonProps & {
    as: 'a';
    href: string;
    target?: '_self' | '_blank' | '_parent' | '_top';
    rel?: string;
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export type ActionProps = ButtonProps | InternalLinkProps | ExternalLinkProps;

function joinClasses(...parts: Array<string | undefined | null | false>): string {
    return parts.filter(Boolean).join(' ');
}
function getDefaultIconSize(size: ActionSize): number {
    switch (size) {
        case 'sm':
            return 18;
        case 'lg':
            return 22;
        case 'md':
        default:
            return 20;
    }
}
function isIconOnly(children: React.ReactNode, iconSrc?: string): boolean {
    if (!iconSrc) return false;
    if (children === null || children === undefined) return true;
    if (typeof children === 'string') return children.trim().length === 0;
    return false;
}

export const Action: React.FC<ActionProps> = (props) => {
    const {
        children,
        extraClass,
        variant = 'outline',
        tone = 'light',
        size = 'md',
        fullWidth = false,
        align = 'center',
        iconSrc,
        iconAlt = '',
        iconPosition = 'left',
        iconSize,
        disabled = false,
        loading = false,
        title,
        ariaLabel,
    } = props;

    const iconOnly = isIconOnly(children, iconSrc);
    const computedIconSize = iconSize ?? getDefaultIconSize(size);
    const isDisabled = disabled || loading;

    // для icon-only требуем ariaLabel
    const resolvedAriaLabel = iconOnly ? ariaLabel : ariaLabel;
    if (iconOnly && !resolvedAriaLabel) {
        // eslint-disable-next-line no-console
        console.warn('[Action] icon-only requires ariaLabel prop for accessibility.');
    }

    const className = joinClasses(
        'action',
        `action--variant-${variant}`,
        `action--tone-${tone}`,
        `action--size-${size}`,
        `action--align-${align}`,
        fullWidth && 'action--full',
        iconOnly && 'action--iconOnly',
        loading && 'action--loading',
        extraClass,
    );

    const content = (
        <>
            {loading && <span className="action__spinner" aria-hidden="true" />}
            <span className="action__inner">
                {iconSrc && !iconOnly && iconPosition === 'left' && (
                    <img className="action__icon" src={iconSrc} alt={iconAlt} width={computedIconSize} height={computedIconSize} loading="lazy" decoding="async" />
                )}

                {iconOnly && iconSrc ? (
                    <img className="action__icon" src={iconSrc} alt={iconAlt} width={computedIconSize} height={computedIconSize} loading="lazy" decoding="async" />
                ) : (
                    <span className="action__label">{children}</span>
                )}

                {iconSrc && !iconOnly && iconPosition === 'right' && (
                    <img className="action__icon" src={iconSrc} alt={iconAlt} width={computedIconSize} height={computedIconSize} loading="lazy" decoding="async" />
                )}
            </span>
        </>
    );

    const commonA11yProps: {
        title?: string;
        'aria-label'?: string;
        'aria-busy'?: true;
        'aria-disabled'?: true;
    } = {
        title,
        ...(resolvedAriaLabel ? { 'aria-label': resolvedAriaLabel } : {}),
        ...(loading ? { 'aria-busy': true } : {}),
        ...(isDisabled ? { 'aria-disabled': true } : {}),
    };

    // Внутренняя ссылка (react-router-dom)
    if (props.as === 'link') {
        const { to, replace, state, onClick } = props;

        return (
            <Link
                to={to}
                replace={replace}
                state={state}
                className={className}
                onClick={(e) => {
                    if (isDisabled) {
                        e.preventDefault();
                        return;
                    }
                    onClick?.(e);
                }}
                {...commonA11yProps}
            >
                {content}
            </Link>
        );
    }

    // Внешняя ссылка (a)
    if (props.as === 'a') {
        const { href, target, rel, onClick } = props;
        const safeRel = target === '_blank' ? (rel ?? 'noopener noreferrer') : rel;

        return (
            <a
                href={isDisabled ? undefined : href}
                target={target}
                rel={safeRel}
                className={className}
                onClick={(e) => {
                    if (isDisabled) {
                        e.preventDefault();
                        return;
                    }
                    onClick?.(e);
                }}
                {...commonA11yProps}
            >
                {content}
            </a>
        );
    }

    // Кнопка
    const { type = 'button', onClick } = props;

    return (
        <button type={type} className={className} onClick={onClick} disabled={isDisabled} {...commonA11yProps}>
            {content}
        </button>
    );
};

export default Action;
